import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import { canEditClass, getSelectedDepartmentId, getSundaySchoolSession } from '../sundaySchoolSession';
import ExcelBulkImport from '../common/ExcelBulkImport';

type Member = {
  id: number;
  name: string;
  birthdate: string | null;
  className: string | null;
  age: number | null;
  allergyLabel: string;
  allergyOk: number | boolean;
  parentLinked: number | boolean;
  tagLabel: string;
};

type Guardian = {
  id: number;
  name: string;
  relation: string;
  phone: string;
  linked: number | boolean;
  portraitConsent: number | boolean;
};

type Milestone = {
  id: number;
  date: string;
  title: string;
  description: string;
};

type Note = {
  id: number;
  authorName: string;
  body: string;
  createdAt: string;
};

type Detail = Member & {
  registeredAt: string | null;
  guardians: Guardian[];
  milestones: Milestone[];
  notes: Note[];
};

type ClassRow = {
  id: number;
  name: string;
};

export default function SundaySchoolMembers() {
  const session = getSundaySchoolSession();
  const [members, setMembers] = useState<Member[]>([]);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [search, setSearch] = useState('');
  const [showReg, setShowReg] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);

  const [form, setForm] = useState({
    name: '',
    birthdate: '',
    classId: '',
    guardianName: '',
    relation: '어머니',
    parentPhone: '',
    allergyLabel: '',
  });
  const [registered, setRegistered] = useState(false);

  const loadMembers = () => {
    if (!session) return;
    axios
      .get(`${MainURL}/sundayschool/students`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
          search,
        },
      })
      .then(({ data }) => {
        if (data.ok) {
          setMembers(data.data);
          if (!selectedId && data.data[0]) setSelectedId(data.data[0].id);
        }
      })
      .catch((err) => console.error('GET /sundayschool/students', err));
  };

  useEffect(() => {
    if (!session) return;
    axios
      .get(`${MainURL}/sundayschool/church`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        },
      })
      .then(({ data }) => {
        if (data.ok) {
          setClasses(data.data.classes);
          // 등록 폼의 반은 내가 등록할 수 있는 반 중 첫 반(교사는 맡은 반).
          const firstEditable = (data.data.classes as ClassRow[]).find((c) => canEditClass(session, c.id));
          setForm((f) => ({ ...f, classId: String(firstEditable?.id ?? '') }));
        }
      })
      .catch((err) => console.error('GET /sundayschool/church', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!selectedId || !session) return;
    axios
      .get(`${MainURL}/sundayschool/students/${selectedId}`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        },
      })
      .then(({ data }) => {
        if (data.ok) setDetail(data.data);
      })
      .catch((err) => console.error('GET /sundayschool/students/:id', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const onRegister = async () => {
    if (!session || !form.name || !form.classId || !form.guardianName || !form.parentPhone) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/students`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
        ...form,
      });
      if (data.ok) {
        setRegistered(true);
        loadMembers();
      }
    } catch (err) {
      console.error('POST /sundayschool/students', err);
    }
  };

  const selected = members.find((m) => m.id === selectedId);
  // 부서담당자 이상은 부서의 모든 반, 교사는 맡은 반만 등록할 수 있고 스텝은 보기만 한다.
  const editableClasses = classes.filter((c) => canEditClass(session, c.id));
  const canRegister = editableClasses.length > 0;

  return (
    <SundaySchoolLayout footNote="교적관리 · 학부모 연결, 초상권 동의 관리">
      <div className="admin-members">
        <div className="page-head">
          <div>
            <h1>교적관리 👥</h1>
            <div className="sub">전체 {members.length}명</div>
          </div>
          <div className="flex">
            <input
              className="input search-input"
              placeholder="🔍 이름 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {canRegister && (
              <>
                <button className="btn btn-outline" onClick={() => setShowBulk((v) => !v)}>
                  📥 엑셀로 일괄 등록
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowReg((v) => !v);
                    setRegistered(false);
                  }}
                >
                  + 신규 등록
                </button>
              </>
            )}
          </div>
        </div>

        {showBulk && canRegister && (
          <ExcelBulkImport
            title="📥 학생 엑셀 일괄 등록"
            hint={`양식의 1행(제목)은 그대로 두고 2행부터 입력하세요. 반 이름은 등록할 수 있는 반(${editableClasses
              .map((c) => c.name)
              .join(
                ', ',
              )})과 정확히 같아야 하며, 생년월일은 2019-03-12 형식입니다. 필수: 이름·반·보호자 이름·학부모 연락처.`}
            templateFileName="학생_일괄등록_양식.xlsx"
            columns={[
              { key: 'name', header: '이름', example: '김하늘' },
              { key: 'birthdate', header: '생년월일', example: '2019-03-12' },
              {
                key: 'className',
                header: '반',
                example: editableClasses[0]?.name ?? '새싹반',
              },
              { key: 'guardianName', header: '보호자 이름', example: '김미영' },
              { key: 'relation', header: '보호자 관계', example: '어머니' },
              {
                key: 'parentPhone',
                header: '학부모 연락처',
                example: '010-1234-5678',
              },
              {
                key: 'allergyLabel',
                header: '특이사항',
                example: '땅콩 알레르기',
              },
            ]}
            onClose={() => setShowBulk(false)}
            onSubmit={async (rows) => {
              if (!session) return null;
              try {
                const { data } = await axios.post(`${MainURL}/sundayschool/students/bulk`, {
                  authChurch: session.user.authChurch,
                  departmentId: getSelectedDepartmentId(),
                  userId: session.user.id,
                  rows,
                });
                if (!data.ok) return null;
                loadMembers();
                return data.data.results;
              } catch (err) {
                console.error('POST /sundayschool/students/bulk', err);
                return null;
              }
            }}
          />
        )}

        {showReg && canRegister && (
          <div className="card card-pad mb16">
            <div className="card-title">
              ➕ 새 학생 등록{' '}
              <button className="btn btn-ghost btn-sm" onClick={() => setShowReg(false)}>
                닫기
              </button>
            </div>
            <div className="grid3">
              <div className="field">
                <label>이름</label>
                <input
                  className="input"
                  placeholder="예) 김하늘"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="field">
                <label>생년월일</label>
                <input
                  className="input"
                  placeholder="2019-03-12"
                  value={form.birthdate}
                  onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                />
              </div>
              <div className="field">
                <label>반</label>
                <select
                  className="select"
                  value={form.classId}
                  onChange={(e) => setForm({ ...form, classId: e.target.value })}
                >
                  {editableClasses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>학부모 연락처</label>
                <input
                  className="input"
                  placeholder="010-1234-5678"
                  value={form.parentPhone}
                  onChange={(e) => setForm({ ...form, parentPhone: e.target.value })}
                />
              </div>
              <div className="field">
                <label>보호자 이름</label>
                <input
                  className="input"
                  placeholder="예) 김미영"
                  value={form.guardianName}
                  onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
                />
              </div>
              <div className="field">
                <label>보호자 관계</label>
                <select
                  className="select"
                  value={form.relation}
                  onChange={(e) => setForm({ ...form, relation: e.target.value })}
                >
                  <option>어머니</option>
                  <option>아버지</option>
                  <option>조부모</option>
                  <option>기타</option>
                </select>
              </div>
              <div className="field">
                <label>특이사항 (알레르기 등)</label>
                <input
                  className="input"
                  placeholder="땅콩 알레르기"
                  value={form.allergyLabel}
                  onChange={(e) => setForm({ ...form, allergyLabel: e.target.value })}
                />
              </div>
            </div>
            <div className="between mt16">
              <span className="muted register-hint">
                학부모는 앱에서 자녀 이름과 여기 입력한 연락처로 가입하면 자동으로 연결됩니다.
              </span>
              <button className="btn btn-primary" onClick={onRegister}>
                {registered ? '✅ 등록 완료!' : '등록'}
              </button>
            </div>
          </div>
        )}

        <div className="grid2 roster-layout">
          <div className="card">
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>학생</th>
                    <th>생일</th>
                    <th>반</th>
                    <th>알레르기</th>
                    <th>학부모</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr
                      key={m.id}
                      onClick={() => setSelectedId(m.id)}
                      className={`member-row${m.id === selectedId ? ' selected' : ''}`}
                    >
                      <td>
                        <div className="name-cell">
                          <span className="avatar av-1">{m.name.slice(0, 1)}</span>
                          <div>
                            {m.name} {m.tagLabel && <span className="chip">{m.tagLabel}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="muted">{m.birthdate ?? '-'}</td>
                      <td>{m.className ?? '-'}</td>
                      <td>
                        <span className={`badge ${m.allergyOk ? 'badge-ok' : 'badge-danger'}`}>
                          {m.allergyLabel || '없음'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${m.parentLinked ? 'badge-sky' : 'badge-warn'}`}>
                          {m.parentLinked ? '연결됨' : '미연결'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selected && detail && (
            <div className="card card-pad">
              <div className="card-title">
                🌟 학생 상세 <span className="chip">선택: {selected.name}</span>
              </div>
              <div className="flex detail-head">
                <span className="avatar avatar-lg av-1">{selected.name.slice(0, 1)}</span>
                <div>
                  <div className="detail-name">{selected.name}</div>
                  <div className="muted detail-sub">
                    {detail.className} {detail.age}세 · {detail.birthdate} · {detail.registeredAt} 등록
                  </div>
                </div>
              </div>

              <div className="section-title detail-section-title">🕊️ 신앙 여정</div>
              <div className="tl">
                {detail.milestones.length === 0 && <div className="muted">기록이 없어요</div>}
                {detail.milestones.map((m) => (
                  <div className="tl-item" key={m.id}>
                    <div className="tl-time">{m.date}</div>
                    <div className="tl-title">{m.title}</div>
                    {m.description && <div className="tl-sub">{m.description}</div>}
                  </div>
                ))}
              </div>

              <div className="section-title detail-section-title">🔔 교사 메모 (학부모 비공개)</div>
              <div className="card teacher-note-card">
                <div className="tl">
                  {detail.notes.length === 0 && <div className="muted">메모가 없어요</div>}
                  {detail.notes.map((n) => (
                    <div className="tl-item" key={n.id}>
                      <div className="tl-time">{n.createdAt.slice(5, 10)}</div>
                      <div className="tl-title">{n.authorName}</div>
                      <div className="tl-sub">{n.body}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="section-title detail-section-title">👪 가족 · 학부모</div>
              <div className="row-list">
                {detail.guardians.map((g) => (
                  <div className="row" key={g.id}>
                    <span className="avatar avatar-sm av-3">{g.name.slice(0, 1)}</span>
                    <div className="grow">
                      <div className="t">
                        {g.name} ({g.relation})
                      </div>
                      <div className="s">
                        {g.phone} · {g.linked ? '계정 연결됨' : '미연결'}
                      </div>
                    </div>
                    {g.linked && (
                      <span className="badge badge-ok">{g.portraitConsent ? '초상권 동의' : '동의 대기'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </SundaySchoolLayout>
  );
}
